import { AppBar, Toolbar, Typography, Box, IconButton, Menu, MenuItem, Avatar } from '@mui/material';
import { Logout, Dashboard as DashboardIcon, Menu as MenuIcon } from '@mui/icons-material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { logout } from '../../store/slices/authSlice';
import HealthIndicator from '../common/HealthIndicator';

interface NavbarProps {
  onMenuButtonClick: () => void;
  isDevelopmentSection?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ onMenuButtonClick, isDevelopmentSection = false }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    handleMenuClose();
    navigate('/login');
  };

  const handleDashboard = () => {
    navigate('/dashboard');
    handleMenuClose();
  };

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        zIndex: (theme) => theme.zIndex.drawer + 1,
        ...(isDevelopmentSection && {
          backgroundColor: '#d32f2f', // Red color for development sections
          '&:hover': {
            backgroundColor: '#b71c1c' // Darker red on hover
          }
        })
      }}
    >
      <Toolbar>
        {/* Hamburger menu button - visible only on mobile */}
        <IconButton
          color="inherit"
          aria-label="apri menu"
          edge="start"
          onClick={onMenuButtonClick}
          sx={{ mr: 2, display: { md: 'none' } }}
        >
          <MenuIcon />
        </IconButton>

        <DashboardIcon sx={{ mr: 2, display: { xs: 'none', md: 'block' } }} />
        <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
          SMS - Scadenziario Manager
          {isDevelopmentSection && (
            <Typography 
              component="span" 
              variant="caption" 
              sx={{ 
                ml: 2, 
                px: 1, 
                py: 0.5, 
                backgroundColor: 'rgba(255,255,255,0.2)', 
                borderRadius: 1,
                fontSize: '0.7rem',
                fontWeight: 'bold'
              }}
            >
              DEV
            </Typography>
          )}
        </Typography>

        {isAuthenticated && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {user && (
              <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
                {user.nome} {user.cognome} ({user.ruolo})
              </Typography>
            )}

            <HealthIndicator />

            <IconButton
              size="large"
              aria-label="account menu"
              aria-controls="account-menu"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                {user ? user.nome.charAt(0) : '?'}
              </Avatar>
            </IconButton>

            <Menu
              id="account-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={handleDashboard}>
                <DashboardIcon sx={{ mr: 1 }} fontSize="small" />
                Dashboard
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <Logout sx={{ mr: 1 }} fontSize="small" />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
